import { useRef } from "react";
import { motion, useScroll, useTransform, useReducedMotion } from "framer-motion";
import { CalendarDays, MapPin, Hash, ArrowDown } from "lucide-react";
import { IMAGES, scrollToId, REGISTER_URL } from "@/data/site";
import { useI18n } from "@/i18n/LanguageContext";
import { Button } from "@/components/ui/button";

export const Hero = () => {
  const { t } = useI18n();
  const ref = useRef(null);
  const reduce = useReducedMotion();
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const contentY = useTransform(scrollYProgress, [0, 1], ["0%", "12%"]);
  const fade = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  return (
    <section id="home" ref={ref} className="relative flex min-h-[90vh] items-center overflow-hidden bg-navy-deep">
      <motion.div className="absolute inset-0 z-0" style={reduce ? undefined : { y: bgY }}>
        <img
          src={IMAGES.hero}
          alt="Abstract neural network representing artificial intelligence in healthcare"
          className="h-[120%] w-full object-cover"
          loading="eager"
        />
        <div className="absolute inset-0" style={{ background: "var(--gradient-hero)" }} />
        <div className="absolute inset-0 bg-grid-light opacity-40" />
      </motion.div>

      <motion.div
        className="container-site section-pad relative z-10 w-full py-28 md:py-32"
        style={reduce ? undefined : { y: contentY, opacity: fade }}
      >
        <div className="max-w-3xl">
          <div className="flex items-center gap-3">
            <span className="gold-rule" aria-hidden="true" />
            <span className="text-xs font-semibold uppercase tracking-[0.28em] text-gold-soft">{t.hero.scheme}</span>
          </div>

          <p className="mt-6 text-sm font-medium tracking-[0.12em] text-primary-foreground/70">{t.hero.kicker}</p>

          <h1 className="font-display mt-4 text-4xl font-medium leading-[1.1] text-primary-foreground text-balance sm:text-5xl lg:text-6xl">
            {t.hero.title}
          </h1>

          <p className="mt-6 max-w-xl text-base leading-relaxed text-primary-foreground/75 sm:text-lg">
            {t.hero.schemeFull}
          </p>

          <div className="mt-8 flex flex-wrap items-center gap-x-6 gap-y-3 text-sm text-primary-foreground/85">
            <span className="inline-flex items-center gap-2">
              <CalendarDays className="h-4 w-4 text-gold-soft" /> {t.hero.dates}
            </span>
            <span className="inline-flex items-center gap-2">
              <Hash className="h-4 w-4 text-gold-soft" /> {t.hero.fdp}
            </span>
            <span className="inline-flex items-center gap-2">
              <MapPin className="h-4 w-4 text-gold-soft" /> {t.hero.venue}
            </span>
          </div>

          <div className="mt-10 flex flex-col gap-3 sm:flex-row sm:flex-wrap">
            <Button asChild size="lg" variant="gold">
              <a href={REGISTER_URL} target="_blank" rel="noopener noreferrer">{t.hero.register}</a>
            </Button>
            <Button size="lg" variant="heroOutline" onClick={() => scrollToId("schedule")}>
              {t.hero.cta1}
            </Button>
            <Button size="lg" variant="heroOutline" onClick={() => scrollToId("contact")}>
              {t.hero.cta2}
            </Button>
          </div>
        </div>
      </motion.div>

      <button
        onClick={() => scrollToId("about")}
        className="absolute bottom-6 left-1/2 z-10 hidden -translate-x-1/2 flex-col items-center gap-1.5 text-primary-foreground/60 transition-colors hover:text-gold-soft md:flex"
        aria-label="Scroll to content"
      >
        <span className="text-[0.65rem] font-medium uppercase tracking-[0.2em]">{t.hero.scroll}</span>
        <ArrowDown className="h-4 w-4 animate-bounce" style={{ animationDuration: "2.4s" }} />
      </button>
    </section>
  );
};
